/**
 * Cancellation Emails Service
 *
 * Data access layer for cancellation_emails table.
 * Handles cancellation email template operations.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import type { Database } from '../lib/database.types';

export type CancellationEmail = Database['public']['Tables']['cancellation_emails']['Row'];
export type CancellationEmailInsert = Database['public']['Tables']['cancellation_emails']['Insert'];
export type CancellationEmailUpdate = Database['public']['Tables']['cancellation_emails']['Update'];

/**
 * Get all cancellation emails for a merchant
 */
export async function getCancellationEmails(merchantId: string, language?: string) {
  try {
    let query = supabase
      .from('cancellation_emails')
      .select('*')
      .eq('merchant_id', merchantId)
      .order('type', { ascending: true });

    if (language) {
      query = query.eq('language', language);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get a single cancellation email by merchant, type, and language
 */
export async function getCancellationEmailByType(
  merchantId: string,
  type: number,
  language: string = 'en'
) {
  try {
    const { data, error } = await supabase
      .from('cancellation_emails')
      .select('*')
      .eq('merchant_id', merchantId)
      .eq('type', type)
      .eq('language', language)
      .maybeSingle();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new cancellation email
 */
export async function createCancellationEmail(data: CancellationEmailInsert) {
  try {
    const { data: newEmail, error } = await supabase
      .from('cancellation_emails')
      .insert(data)
      .select()
      .single();

    if (error) throw error;

    return { data: newEmail, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update a cancellation email
 */
export async function updateCancellationEmail(id: string, data: CancellationEmailUpdate) {
  try {
    const { data: updatedEmail, error } = await supabase
      .from('cancellation_emails')
      .update({
        ...data,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data: updatedEmail, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get distinct languages that have existing cancellation emails for a merchant
 */
export async function getExistingCancellationEmailLanguages(merchantId: string) {
  try {
    const { data, error } = await supabase
      .from('cancellation_emails')
      .select('language')
      .eq('merchant_id', merchantId);

    if (error) throw error;

    const languages = [...new Set((data || []).map(row => row.language).filter(Boolean))] as string[];
    return { data: languages, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete a cancellation email
 */
export async function deleteCancellationEmail(id: string) {
  try {
    const { error } = await supabase
      .from('cancellation_emails')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Upsert a cancellation email (create or update)
 */
export async function upsertCancellationEmail(data: CancellationEmailInsert) {
  try {
    const { data: upsertedEmail, error } = await supabase
      .from('cancellation_emails')
      .upsert(data, {
        onConflict: 'merchant_id,language,type'
      })
      .select()
      .single();

    if (error) throw error;

    return { data: upsertedEmail, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
